import { useModal } from '../../hooks/modalContext';

import { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  addMFAMethod,
  APP_ENV,
  getTenantDetails,
  sendVerificationCodeEmail,
  sendVerificationCodePhone,
  verifyVerificationCodeEmail,
  verifyVerificationCodePhoneUpdate,
} from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { CANANA_COUNTRY_CODE } from '../../../constants/canadaCountryCode';
import { formatPhoneNumber } from '../../utils/utils';

import { useMFA } from '../../hooks/mfaListContext';
import { showToast } from '../../utils/showToast';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
interface props {
  verificationType: 'email' | 'phone';
  onClose?: () => void;
}

const VerificationBeforeMFA = ({ verificationType, onClose }: props) => {
  const { setModal } = useModal();

  const { auth } = useAuth();

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isPhoneVerification] = useState(verificationType === 'phone');
  const [isEmailVerification] = useState(verificationType === 'email');
  const [errorMessage, setErrorMessage] = useState('');
  const [validMessage, setValidMessage] = useState('');
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingBtn, setLoadingBtn] = useState(false);
  const { setEmailEnabled, setPhoneEnabled } = useMFA();
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  const [inputValues, setInputValues] = useState(['', '', '', '']);
  const handleInputChange = (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.slice(0, 1);
    if (!/^\d$/.test(value)) {
      value = '';
    }
    const newValues = [...inputValues];
    newValues[index] = value;
    setInputValues(newValues);
    if (value.length === 1 && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };
  const handleVerifyEmail = async () => {
    const code = inputValues.join('');
    const isValidCode = code.length === 4 && /^\d{4}$/.test(code);
    if (isValidCode) {
      setErrorMessage('');
      setLoading(true);
      try {
        const token = auth?.data.token || '';
        const response = await verifyVerificationCodeEmail(email, token, code);
        if (response.message === 'verification_successful') {
          setValidMessage('Your email has been verified');
          setIsEmailVerified(true);

          // Adding timeout before enabling MFA
          setTimeout(async () => {
            try {
              setLoading(true);
              await addMFAMethod(auth?.data.token || '', APP_ENV, 'email');
              showToast({
                message: 'Multi-factor authentication has been enabled via email!',
                type: 'success',
              });

              setEmailEnabled(true);
              setModal(null);
            } catch {
              showToast({
                message:
                  'Failed to enable email for Multi-Factor Authentication. Please try again.',
                type: 'error',
              });

              console.error('Error enabling email for MFA');
            } finally {
              setLoading(false);
            }
          }, 2000); // Timeout of 2 seconds before enabling MFA
        } else {
          setErrorMessage(response.errorMessage || 'Verification failed');
        }
      } catch (error) {
        setErrorMessage('Please enter a valid code');
      } finally {
        setLoading(false);
      }
    } else {
      setErrorMessage('Please enter a valid code');
    }
  };

  const handleVerifyPhone = async () => {
    const code = inputValues.join('');
    const isValidCode = code.length === 4 && /^\d{4}$/.test(code);
    if (isValidCode) {
      setErrorMessage('');
      setLoadingBtn(true);
      try {
        const token = auth?.data.token || '';
        setLoading(true);
        const response = await verifyVerificationCodePhoneUpdate(
          `+${CANANA_COUNTRY_CODE}${phone}`,
          token,
          code
        );

        if (response.message === 'verification_success') {
          setValidMessage('Your phone number has been verified');

          // Adding timeout before enabling MFA
          setTimeout(async () => {
            try {
              setLoading(true);
              await addMFAMethod(auth?.data.token || '', APP_ENV, 'phoneNumber');
              showToast({
                message: 'Multi-factor authentication has been enabled via phone number!',
                type: 'success',
              });

              setPhoneEnabled(true);
              setModal(null);
            } catch {
              showToast({
                message:
                  'Failed to enable phone number for Multi-Factor Authentication. Please try again.',
                type: 'error',
              });

              console.error('Error enabling phone for MFA');
            } finally {
              setLoading(false);
            }
          }, 2000); // Timeout of 2 seconds before enabling MFA
        } else {
          setErrorMessage(
            (response.message === 'invalid_verification_code' && 'Please enter a valid code') ||
              'Verification failed'
          );
        }
      } catch (error) {
        setErrorMessage('Please enter a valid code');
      } finally {
        setLoadingBtn(false);
        setLoading(false);
      }
    } else {
      setErrorMessage('Please enter a valid code');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (['+', '-'].includes(e.key)) {
      e.preventDefault();
    } else if (e.key === 'Enter') {
      isPhoneVerification ? handleVerifyPhone() : handleVerifyEmail();
    } else if (e.key === 'Backspace') {
      if (inputValues[index] === '' && index > 0) {
        inputRefs[index - 1].current?.focus();
      }
    }
  };
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('Text');
    const digits = pastedData.replace(/\D/g, '').substring(0, 4).split('');

    const newValues = [...inputValues];
    newValues[0] = digits[0] || '';
    newValues[1] = digits[1] || '';
    newValues[2] = digits[2] || '';
    newValues[3] = digits[3] || '';
    setInputValues(newValues);
    inputRefs[digits.length > 0 ? digits.length - 1 : 0].current?.focus();
  };
  useEffect(() => {
    const fetchDataAndSendCode = async () => {
      try {
        setLoading(true);
        const response = await getTenantDetails(auth?.data.token);
        setEmail(response.data.user.email);
        setPhone(response.data.user.phoneNumber);

        if (response.data.user.phoneNumber) {
          try {
            isPhoneVerification
              ? await sendVerificationCodePhone(
                  `+${CANANA_COUNTRY_CODE}${response.data.user.phoneNumber}`,
                  4,
                  auth?.data.token
                )
              : await sendVerificationCodeEmail(response.data.user.email, 4, auth?.data.token);
          } catch (error) {
            console.error('Error sending verification code', error);
            setErrorMessage('Failed to send verification code');
          }
        }
      } catch (error) {
        console.error('Error fetching tenant phone number and email', error);
        setErrorMessage('Failed to fetch tenant details');
      } finally {
        setLoading(false);
      }
    };

    fetchDataAndSendCode();
  }, []);

  return (
    <motion.main
      className='bg-[#fff]  rounded-[12px] relative  flex flex-col  w-full ph-no-capture'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-clarity-mask='true'
    >
      <section>
        <main className='flex flex-col p-6 gap-2'>
          {loading ? (
            <p>Loading...</p>
          ) : (
            <>
              <div className='w-full flex justify-end'>
                <X
                  className='cursor-pointer'
                  onClick={() => {
                    if (onClose) onClose();
                  }}
                />
              </div>

              <div className='flex flex-col items-center gap-5 mb-4'>
                <div className='text-main text-[20px] font-semibold'>
                  {isPhoneVerification
                    ? 'Verify phone number'
                    : isEmailVerification
                    ? 'Verify email'
                    : ''}
                </div>
                <div className='text-main text-base  text-center'>
                  We have sent a verification code to <br />
                  <span className='text-primary font-semibold'>
                    {isPhoneVerification ? formatPhoneNumber(phone, true) : email}
                  </span>
                  . Please enter the code to verify before enabling multi-factor authentication.
                </div>
              </div>
              <div className='flex flex-col gap-[30px]'>
                <div className='flex flex-col gap-[10px]'>
                  <div className='w-[222px] mx-auto flex justify-between items-center'>
                    {inputRefs.map((ref, index) => (
                      <input
                        key={index}
                        type='number'
                        ref={ref}
                        className='size-[48px] flex items-center justify-center border-[#EBEDF0] border-[1px] px-[12px] rounded-[8px] outline-none text-[#7A8699]
    text-[14px]/[170%] text-center font-semibold'
                        value={inputValues[index]}
                        onChange={handleInputChange(index)}
                        maxLength={1}
                        min={0}
                        max={9}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        onPaste={handlePaste}
                        onWheel={(e) => (e.target as HTMLInputElement).blur()}
                      />
                    ))}
                  </div>
                  {errorMessage && <div className='error text-center'>{errorMessage}</div>}
                  {validMessage && <div className=' text-center'>{validMessage}</div>}
                </div>
                <button
                  className='submitBtn'
                  onClick={() => {
                    isPhoneVerification ? handleVerifyPhone() : handleVerifyEmail();
                  }}
                  disabled={isEmailVerified || inputValues.some((val) => val === '') || loading}
                >
                  {!loadingBtn && <span className='indicator-label'>Verify</span>}
                  {loadingBtn && <span style={{ display: 'block' }}>Please wait...</span>}
                </button>
              </div>
            </>
          )}
        </main>
      </section>
    </motion.main>
  );
};
export default VerificationBeforeMFA;
